'use client';

import React, { useEffect, useState } from 'react';
import { CheckCircle2, XCircle, AlertTriangle, Info, X } from 'lucide-react';
import Icon from '@/components/ui/AppIcon';


export type ToastType = 'success' | 'error' | 'warning' | 'info';

export interface ToastMessage {
  id: string;
  message: string;
  type: ToastType;
  duration?: number;
}

interface ToastItemProps {
  toast: ToastMessage;
  onDismiss: (id: string) => void;
}

const toastConfig: Record<ToastType, { icon: React.ElementType; bg: string; border: string; iconColor: string; textColor: string }> = {
  success: { icon: CheckCircle2, bg: 'bg-surface', border: 'border-success/40', iconColor: 'text-success', textColor: 'text-foreground' },
  error: { icon: XCircle, bg: 'bg-surface', border: 'border-danger/40', iconColor: 'text-danger', textColor: 'text-foreground' },
  warning: { icon: AlertTriangle, bg: 'bg-surface', border: 'border-warning/40', iconColor: 'text-warning', textColor: 'text-foreground' },
  info: { icon: Info, bg: 'bg-surface', border: 'border-primary/40', iconColor: 'text-primary', textColor: 'text-foreground' },
};

function ToastItem({ toast, onDismiss }: ToastItemProps) {
  const [visible, setVisible] = useState(false);
  const cfg = toastConfig[toast.type];
  const Icon = cfg.icon;

  useEffect(() => {
    // Animate in
    const showTimer = setTimeout(() => setVisible(true), 10);
    // Auto-dismiss
    const hideTimer = setTimeout(() => {
      setVisible(false);
      setTimeout(() => onDismiss(toast.id), 300);
    }, toast.duration ?? 3500);
    return () => {
      clearTimeout(showTimer);
      clearTimeout(hideTimer);
    };
  }, [toast.id, toast.duration, onDismiss]);

  return (
    <div
      className={`flex items-start gap-3 px-4 py-3 rounded-xl shadow-xl border ${cfg.bg} ${cfg.border} transition-all duration-300 ease-out min-w-[280px] max-w-[380px]
        ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-2'}`}
    >
      <Icon size={16} className={`${cfg.iconColor} flex-shrink-0 mt-0.5`} />
      <p className={`text-sm flex-1 leading-snug ${cfg.textColor}`}>{toast.message}</p>
      <button
        onClick={() => { setVisible(false); setTimeout(() => onDismiss(toast.id), 300); }}
        className="text-muted hover:text-foreground transition-colors flex-shrink-0 mt-0.5"
      >
        <X size={13} />
      </button>
    </div>
  );
}

interface ToastContainerProps {
  toasts: ToastMessage[];
  onDismiss: (id: string) => void;
}

export function ToastContainer({ toasts, onDismiss }: ToastContainerProps) {
  if (toasts.length === 0) return null;
  return (
    <div className="fixed bottom-6 right-6 z-[100] flex flex-col gap-2 items-end">
      {toasts.map((t) => (
        <ToastItem key={t.id} toast={t} onDismiss={onDismiss} />
      ))}
    </div>
  );
}

// Hook for managing toasts
export function useToast() {
  const [toasts, setToasts] = useState<ToastMessage[]>([]);

  const addToast = (message: string, type: ToastType = 'success', duration?: number) => {
    const id = `toast-${Date.now()}-${Math.random()}`;
    setToasts((prev) => [...prev, { id, message, type, duration }]);
  };

  const dismissToast = (id: string) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  };

  return { toasts, addToast, dismissToast };
}
